import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getApiUrl } from '../config/api'
import { hasSessionHint } from '../utils/session'
import FileDropzone from './common/FileDropzone'
import './ReportIssueWidget.css'

const MAX_FILES = 5

export default function ReportIssueWidget() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [attachments, setAttachments] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [signedIn, setSignedIn] = useState(() => hasSessionHint())
  const fileInputRef = useRef(null)

  useEffect(() => {
    const sync = () => setSignedIn(hasSessionHint())
    sync()
    window.addEventListener('user-updated', sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener('user-updated', sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const resetForm = useCallback(() => {
    setTitle('')
    setDescription('')
    setAttachments([])
    setError(null)
    setSuccess(false)
  }, [])

  const close = useCallback(() => {
    setOpen(false)
    resetForm()
  }, [resetForm])

  const addFile = useCallback((file) => {
    setAttachments((prev) => {
      if (prev.length >= MAX_FILES) {
        setError(`Maximum ${MAX_FILES} attachments`)
        return prev
      }
      if (prev.some((f) => f.name === file.name && f.size === file.size)) return prev
      setError(null)
      return [...prev, file]
    })
  }, [])

  const removeFile = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!hasSessionHint()) {
      setError('Please sign in to report an issue.')
      return
    }
    if (!title.trim()) {
      setError('Title is required.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('title', title.trim())
      formData.append('description', description.trim())
      formData.append('page_url', window.location.href)
      formData.append('user_agent', navigator.userAgent || '')
      attachments.forEach((file) => formData.append('files', file))

      const res = await fetch(getApiUrl('/api/issues'), {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to submit issue')
      }
      setSuccess(true)
      setTimeout(() => close(), 1800)
    } catch (err) {
      setError(err.message || 'Failed to submit issue')
    } finally {
      setSubmitting(false)
    }
  }

  if (!signedIn || location.pathname.startsWith('/login') || location.pathname.startsWith('/signup')) {
    return null
  }

  return (
    <>
      <button
        type="button"
        className="report-issue-fab"
        onClick={() => { setOpen(true); setSuccess(false) }}
        aria-label="Report an issue"
        title="Report an issue"
      >
        <i className="bx bx-bug report-issue-fab-icon" aria-hidden />
      </button>

      {open && (
        <>
          <div className="report-issue-backdrop" onClick={() => !submitting && close()} aria-hidden />
          <div className="report-issue-panel" role="dialog" aria-labelledby="report-issue-title" aria-modal="true">
            <div className="report-issue-header">
              <h2 id="report-issue-title">Report an issue</h2>
              <button type="button" className="report-issue-close" onClick={close} disabled={submitting} aria-label="Close">
                ×
              </button>
            </div>

            {success ? (
              <div className="report-issue-success">
                <p>Thanks — your report was submitted.</p>
              </div>
            ) : (
              <form className="report-issue-form" onSubmit={handleSubmit}>
                <div className="report-issue-field">
                  <label htmlFor="report-issue-title-input">Title</label>
                  <input
                    id="report-issue-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Brief summary"
                    maxLength={200}
                    disabled={submitting}
                    autoFocus
                  />
                </div>

                <div className="report-issue-field">
                  <label htmlFor="report-issue-description">Description</label>
                  <textarea
                    id="report-issue-description"
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What happened? Steps to reproduce, expected vs actual behavior…"
                    disabled={submitting}
                  />
                </div>

                <div className="report-issue-field">
                  <label>Screenshot / attachments</label>
                  <FileDropzone
                    accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv"
                    maxSizeMB={10}
                    loading={submitting}
                    loadingLabel="Submitting…"
                    onFile={addFile}
                    onError={setError}
                    title="Drag & drop screenshots or files"
                    hint={`Up to ${MAX_FILES} files, 10 MB each`}
                    size="sm"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="report-issue-file-input"
                    accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv"
                    multiple
                    onChange={(e) => {
                      Array.from(e.target.files || []).forEach(addFile)
                      e.target.value = ''
                    }}
                  />
                  {attachments.length > 0 && (
                    <ul className="report-issue-file-list">
                      {attachments.map((file, i) => (
                        <li key={`${file.name}-${file.size}-${i}`}>
                          <span>{file.name}</span>
                          <button type="button" onClick={() => removeFile(i)} disabled={submitting}>
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {error && <p className="report-issue-error" role="alert">{error}</p>}

                <div className="report-issue-footer">
                  <button type="button" className="report-issue-btn-secondary" onClick={close} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="report-issue-btn-primary" disabled={submitting || !title.trim()}>
                    {submitting ? 'Submitting…' : 'Submit report'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </>
  )
}
